// Section 22 event prompt and Section 14 arrival screen (stream E2). Every open event prompt with
// its text, choices, one-line previews and, for a group at the gate, the candidates; the answer
// drafted so far; and which prompts are still unanswered (the End season button is disabled with
// a reason while any open prompt is unanswered, Section 22). Pure: (game, draft) -> view.
import {
  eventPromptViews,
  type Config,
  type Decision,
  type EventPromptView,
  type GameState,
  type MigrationOption,
  type PersonId,
  type Prompt,
} from '../sim/api';
import { memo } from './memo';

export const MIGRATION_OPTIONS: readonly MigrationOption[] = [
  'open',
  'skilled-only',
  'families-only',
  'closed',
  'ask',
];

export interface DraftedAnswer {
  choiceId: string;
  personIds: PersonId[];
}

export interface EventPromptVM extends EventPromptView {
  answer: DraftedAnswer | null;
}

export interface EventsVM {
  prompts: EventPromptVM[];
  answered: number;
  total: number;
  /** The migration policy as drafted (Section 18) and whether plague closed the gates. */
  migration: MigrationOption;
  gatesClosedUntilTurn: number | null;
}

/**
 * Open prompts of any kind without an answer in the draft (events, justice cases, pairings):
 * `validateDecision` rejects a Decision that leaves one unanswered, so End season waits for them.
 */
export function unansweredPrompts(game: GameState, draft: Decision | null): Prompt[] {
  const answers = draft?.eventChoices ?? {};
  return game.openPrompts.filter((p) => answers[p.id] === undefined);
}

/** Only the event prompts still unanswered (the Event prompt overlay's count). */
export function unansweredEventPrompts(game: GameState, draft: Decision | null): Prompt[] {
  return unansweredPrompts(game, draft).filter((p) => p.kind === 'event');
}

const views = memo((game: GameState, cfg: Config | undefined) => eventPromptViews(game, cfg));

export function eventsVM(game: GameState, draft: Decision | null, cfg?: Config): EventsVM {
  const answers = draft?.eventChoices ?? {};
  const prompts = views(game, cfg).map((view): EventPromptVM => {
    const a = answers[view.promptId];
    return {
      ...view,
      answer: a?.kind === 'event' ? { choiceId: a.choiceId, personIds: [...a.personIds] } : null,
    };
  });
  return {
    prompts,
    answered: prompts.filter((p) => p.answer !== null).length,
    total: prompts.length,
    migration: draft?.policies.migration ?? game.policies.migration,
    gatesClosedUntilTurn: game.counters.gatesClosedUntilTurn,
  };
}
